import { DDSButton, DDSTypography } from 'den-design-system'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  convertStorage,
  parseFileInfoFromUrl
} from '../../../utils/IncidentUtil'
import {
  getAllCommentsOfIncident,
  getAnIncident
} from '../../../api/incident/incident.service'
import { getUserTimeZoneDate, timeZone } from '../../../utils/DateTimeUtils'

import Card from '../../../components/common/IncidentCard/IncidentCard'
import { Delta } from 'quill'
import DocumentHeader from '../../../components/Helmet/DocumentHeader'
import { Download } from '@carbon/icons-react'
import FilePreviewCard from '../../IncidentPage/FileTypes'
import { HeaderConstants } from '../../../constants/HeaderConstants'
import MediaPreviewGrid from '../../../components/common/TextAreaDrafts/MediaPreviewGrid'
import MediaPreviewModal from '../../../components/common/TextAreaDrafts/MediaPreviewModal'
import ReadOnlyQuill from '../../../components/common/TextAreaDrafts/ReadOnlyQuill'
import Spinner from '../../../components/common/Spinner/Spinner'
import { ToastMessageConstants } from '../../../constants/ToastMessageConstants'
import ToastNotification from '../../../components/common/DDS/Toast/Toast'
import html2pdf from 'html2pdf.js'
import { isUndefined } from 'lodash'
import moment from 'moment'
import pallete from '../../../global/newPallete'
import { replaceUrl } from '../../../utils/UrlUtils'
import routeConstants from '../../../constants/RouteConstants'
import { timeFormats } from '../../../enum/TIME'
import { useParams } from 'react-router-dom'
import { useStoreContext } from '../../../store/StoreContext'
import vigilLogo from '../../../assets/images/vigilLogo.png'

const IncidentOverView: React.FC = () => {
  const { incidentId } = useParams()
  const [incidentDetails, setIncidentDetails] = useState<any>({})
  const [activityMessages, setActivityMessages] = React.useState<any>()
  const [loading, setLoading] = useState<boolean>(true)
  const store = useStoreContext()
  const [download, setDownload] = useState<boolean>(false)
  const [previewFile, setPreviewFile] = useState<any>(null)
  const [isPreviewOpen, setPreviewOpen] = useState(false)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const orgTimeZone: string =
    store.userStore.getOrganizationPreference()?.timeZone

  const downloadPdf = () => {
    setDownload(true)

    const element = document.getElementById('pdfContent')
    const options = {
      margin: [10, 10, 11, 10],
      filename: 'incident-overview.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 4,
        dpi: 300,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'landscape'
      }
    }

    html2pdf()
      .from(element)
      .set(options)
      .toPdf()
      .get('pdf')
      .then((pdf: any) => {
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const imageWidth = 162.095
        const imageHeight = 25
        const pxToMm = (px: number) => (px * 25.4) / 96
        const imageWidthMm = pxToMm(imageWidth)
        const imageHeightMm = pxToMm(imageHeight)
        const imageX = pdfWidth - imageWidthMm - 10
        const imageY = 10
        pdf.setPage(1)
        pdf.addImage(
          vigilLogo,
          'PNG',
          imageX,
          imageY,
          imageWidthMm,
          imageHeightMm
        )
      })
      .save()
      .finally(() => {
        setDownload(false)
      })
  }

  const handlePreview = (url: string) => {
    setPreviewOpen(true)
    store.uiStore.setSidebarExpanded(false)
    setPreviewFile(url)
  }
  const handleClosePreview = () => {
    setPreviewOpen(false)
    store.uiStore.setSidebarExpanded(true)
    setPreviewFile(null)
  }

  const fetchAnIncident = () => {
    setLoading(true)
    getAnIncident({ $incidentId: incidentId })
      .then((response: any) => {
        setIncidentDetails(response.data)
      })
      .catch((error: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.GET_ALL_INCIDENTS.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const created = moment(incidentDetails?.createdAt).tz(orgTimeZone)
  const resolved = moment(incidentDetails?.resolvedAt).tz(orgTimeZone)

  const duration = moment.duration(resolved.diff(created))
  const timeToResolution =
    [
      duration.days()
        ? `${duration.days()} day${duration.days() > 1 ? 's' : ''}`
        : null,
      duration.hours()
        ? `${duration.hours()} hour${duration.hours() > 1 ? 's' : ''}`
        : null,
      duration.minutes()
        ? `${duration.minutes()} minute${duration.minutes() > 1 ? 's' : ''}`
        : null
    ]
      .filter(Boolean)
      .join(' ') || '1 minute'

  const nowInTZ = moment().tz(orgTimeZone)
  const timeSinceIdentification = moment.duration(nowInTZ.diff(created))

  const formattedTimeSince =
    [
      timeSinceIdentification.days()
        ? `${timeSinceIdentification.days()} day${timeSinceIdentification.days() > 1 ? 's' : ''}`
        : null,
      timeSinceIdentification.hours()
        ? `${timeSinceIdentification.hours()} hour${timeSinceIdentification.hours() > 1 ? 's' : ''}`
        : null,
      timeSinceIdentification.minutes()
        ? `${timeSinceIdentification.minutes()} minute${timeSinceIdentification.minutes() > 1 ? 's' : ''}`
        : null
    ]
      .filter(Boolean)
      .join(' ') || '1 minute'

  const fetchAllActivities = () => {
    setLoading(true)
    getAllCommentsOfIncident({ $incidentId: incidentId as string })
      .then((response: any) => {
        setActivityMessages(response.data)
      })
      .catch((error: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.GET_ALL_INCIDENTS.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }
  useEffect(() => {
    if (!isUndefined(incidentDetails.incidentNumber)) {
      store.breadcrumbStore.setMultipleBreadcrumbsOptions([
        {
          label: 'Incidents',
          link:
            replaceUrl(
              routeConstants.INCIDENT,
              store.scopeStore.getScope()['organization-id'] as string,
              store.scopeStore.getScope()['project-id'] as string,
              store.scopeStore.getScope()['environment-id'] as string
            ) + '?page=1&tab=1'
        },
        {
          label: incidentDetails?.incidentNumber,
          link: replaceUrl(
            routeConstants.MANAGE_INCIDENTS,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ).replace(':incidentId', String(incidentId))
        },
        {
          label: 'Post-mortem',
          link: replaceUrl(
            routeConstants.UPDATE_INCIDENT,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          )
        }
      ])
    }
  }, [store.breadcrumbStore.getBreadcrumbsOptions(), incidentDetails])

  useEffect(() => {
    fetchAllActivities()
    fetchAnIncident()
  }, [])

  const Chip = ({ children }: { children: React.ReactNode }) => (
    <span className='px-[2px] py-[2px] text-[12px] rounded-[2px] bg-[#F5F7F9] text-[#586679] font-medium whitespace-nowrap'>
      {children}
    </span>
  )

  const statusWords = ['Monitoring', 'Resolved', 'In Progress', 'Identified']

  const renderWithFromToChip = (message: string): React.ReactNode => {
    const match = message.match(/from\s+(.*?)\s+to\s+(.*)/i)
    if (!match) return null

    const [_, from, to] = match
    const before = message.split(match[0])[0]
    const chipText = `${from} to ${to}`

    return (
      <>
        {before}
        from <Chip>{chipText}</Chip>
      </>
    )
  }

  const renderWithIndividualChips = (message: string): React.ReactNode => {
    const sortedStatuses = [...statusWords].sort((a, b) => b.length - a.length)

    let result: React.ReactNode[] = [message]

    sortedStatuses.forEach((status) => {
      result = result.flatMap((part: any, index: any) => {
        if (typeof part !== 'string') return [part]

        const split = part.split(new RegExp(`(${status})`, 'gi'))
        return split.map((chunk, i) =>
          chunk.toLowerCase() === status.toLowerCase() ? (
            <Chip key={`${status}-${index}-${i}`}>{chunk}</Chip>
          ) : (
            chunk
          )
        )
      })
    })

    return result
  }

  const createAutoDownloadBlobUrl = (attachedFiles: string[]) => {
    const html = `
    <html>
      <head><title>Downloading Files...</title></head>
      <body>
        <h2>Downloading all files...</h2>
        <p>Please wait while files are being downloaded...</p>
        <script>
          const files = ${JSON.stringify(attachedFiles)};
          const delay = (ms) => new Promise(res => setTimeout(res, ms));

          const getFileName = (url, index) => {
            try {
              const name = decodeURIComponent(url.split('/').pop());
              return name.includes('.') ? name : 'file-' + (index + 1);
            } catch {
              return 'file-' + (index + 1);
            }
          };

          const downloadFile = (url, name) => {
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          };

          const downloadAll = async () => {
            for (let i = 0; i < files.length; i++) {
              const url = files[i];
              const name = getFileName(url, i);
              downloadFile(url, name);
              await delay(1500);
            }
            setTimeout(() => {
              document.body.innerHTML = '<h2>All downloads completed!</h2>';
            }, 2000);
          };

          window.onload = () => downloadAll();
        </script>
      </body>
    </html>
  `

    const blob = new Blob([html], { type: 'text/html' })
    return URL.createObjectURL(blob) // return the blob URL
  }

  const blobUrl = useMemo(
    () => createAutoDownloadBlobUrl(incidentDetails.attachedFiles),
    [incidentDetails.attachedFiles]
  )

  return (
    <>
      <DocumentHeader header={HeaderConstants.INCIDENT} />
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div
          className='relative h-[100%] flex flex-col py-[24px]'
          id='pdfContent'
        >
          <div>
            <div className='flex flex-row pb-[8px] justify-between'>
              <DDSTypography.Title
                type='h3'
                variant='bold'
                className='max-w-[600px]'
                color={pallete.colors.textDark3}
              >
                {incidentDetails.briefDescription}
              </DDSTypography.Title>
              <div
                className='flex flex-row gap-x-[8px]'
                data-html2canvas-ignore='true'
              >
                <DDSButton
                  label={
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.white}
                    >
                      Download
                    </DDSTypography.Paragraph>
                  }
                  type='filled'
                  shape='roundedRectangle'
                  onClick={downloadPdf}
                  buttonHoverTextColor={pallete.colors.textDark10}
                  size='small'
                  style={{
                    color: pallete.colors.white
                  }}
                  disabled={store.userStore.getNoActiveSubscription()}
                  className='whitespace-nowrap !min-w-0'
                  buttonHoverBackgroundColor={pallete.colors.fillButtonhover}
                  startIcon={<Download color={pallete.colors.white} />}
                />
              </div>
            </div>
            <div className='flex flex-row gap-x-[12px] items-baseline'>
              <div className='flex flex-row gap-x-[8px] items-baseline'>
                <DDSTypography.Paragraph
                  size='caption'
                  variant='medium'
                  className='whitespace-nowrap'
                  color={pallete.colors.textDark6}
                >
                  Created by:
                </DDSTypography.Paragraph>
                <DDSTypography.Paragraph
                  size='para'
                  variant='semiBold'
                  color={pallete.colors.textDark3}
                  className='whitespace-nowrap'
                >
                  {incidentDetails.createdBy}
                </DDSTypography.Paragraph>
              </div>
              <div className='flex flex-row pl-[12px] border-l gap-x-[8px] items-baseline'>
                <DDSTypography.Paragraph
                  size='caption'
                  variant='medium'
                  color={pallete.colors.textDark6}
                  className='whitespace-nowrap break-all'
                >
                  Created on:
                </DDSTypography.Paragraph>
                <DDSTypography.Paragraph
                  size='para'
                  variant='semiBold'
                  color={pallete.colors.textDark3}
                  className='whitespace-nowrap'
                >
                  {timeZone(
                    store.userStore.getOrganizationPreference().timeZone,
                    incidentDetails.createdAt,
                    timeFormats.fullMonthDateYearTime
                  )}
                </DDSTypography.Paragraph>
              </div>
              {incidentDetails.status === 'Resolved' && (
                <>
                  <div className='flex flex-row pl-[12px] border-l gap-x-[8px] items-baseline'>
                    <DDSTypography.Paragraph
                      size='caption'
                      variant='medium'
                      color={pallete.colors.textDark6}
                      className='whitespace-nowrap break-all'
                    >
                      Resolved on:
                    </DDSTypography.Paragraph>
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.textDark3}
                      className='whitespace-nowrap'
                    >
                      {timeZone(
                        store.userStore.getOrganizationPreference().timeZone,
                        incidentDetails.resolvedAt,
                        timeFormats.fullMonthDateYearTime
                      )}
                    </DDSTypography.Paragraph>
                  </div>
                  <div className='flex flex-row pl-[12px] border-l gap-x-[8px] items-baseline'>
                    <DDSTypography.Paragraph
                      size='caption'
                      variant='medium'
                      color={pallete.colors.textDark6}
                      className='whitespace-nowrap break-all'
                    >
                      Time to Resolution:
                    </DDSTypography.Paragraph>
                    <DDSTypography.Paragraph
                      size='para'
                      variant='semiBold'
                      color={pallete.colors.textDark3}
                      className='whitespace-nowrap'
                    >
                      {timeToResolution}
                    </DDSTypography.Paragraph>
                  </div>
                </>
              )}
              {incidentDetails.status !== 'Resolved' && (
                <div className='flex flex-row pl-[12px] border-l gap-x-[8px] items-baseline'>
                  <DDSTypography.Paragraph
                    size='caption'
                    variant='medium'
                    color={pallete.colors.textDark6}
                    className='whitespace-nowrap break-all'
                  >
                    Time Since Identification:
                  </DDSTypography.Paragraph>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='semiBold'
                    color={pallete.colors.textDark3}
                    className='whitespace-nowrap'
                  >
                    {formattedTimeSince}
                  </DDSTypography.Paragraph>
                </div>
              )}
            </div>
          </div>
          <div className='flex flex-col gap-y-[8px] pt-[24px]'>
            <Card>
              <div className='flex flex-col gap-y-[8px] '>
                <DDSTypography.Title
                  type='h4'
                  variant='bold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Description
                </DDSTypography.Title>
                <DDSTypography.Paragraph
                  variant='regular'
                  size='para'
                  color={pallete.colors.textDark5}
                  style={{ overflowWrap: 'break-word' }}
                >
                  {incidentDetails.longDescription}
                </DDSTypography.Paragraph>
              </div>
            </Card>
          </div>
          {incidentDetails.rootCause &&
            incidentDetails.solution &&
            incidentDetails.status === 'Resolved' && (
              <div className='flex flex-col gap-y-[8px] pt-[24px]'>
                <Card>
                  <div className='flex flex-col gap-y-[8px] '>
                    <DDSTypography.Title
                      type='h4'
                      variant='bold'
                      color={pallete.colors.textDark3}
                      className='custom-label'
                    >
                      Root Cause
                    </DDSTypography.Title>
                    <DDSTypography.Paragraph
                      variant='regular'
                      size='para'
                      color={pallete.colors.textDark5}
                      style={{ overflowWrap: 'break-word' }}
                    >
                      {incidentDetails.rootCause}
                    </DDSTypography.Paragraph>
                  </div>
                  <div className='flex flex-col py-[16px] '>
                    <DDSTypography.Title
                      type='h4'
                      variant='bold'
                      color={pallete.colors.textDark3}
                      className='custom-label'
                    >
                      Solution
                    </DDSTypography.Title>
                    <DDSTypography.Paragraph
                      variant='regular'
                      size='para'
                      color={pallete.colors.textDark5}
                      style={{ overflowWrap: 'break-word' }}
                    >
                      {incidentDetails.solution}
                    </DDSTypography.Paragraph>
                  </div>
                </Card>
              </div>
            )}
          {incidentDetails?.savedLinks?.length > 0 && (
            <div className='pt-[24px]'>
              <Card>
                <DDSTypography.Title
                  type='h4'
                  variant='bold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Reference links
                </DDSTypography.Title>
                <div className='flex flex-col gap-y-[8px]'>
                  {incidentDetails?.savedLinks?.map(
                    (link: any, index: number) => (
                      <DDSTypography.Paragraph
                        key={index}
                        size='para'
                        variant='medium'
                        color={pallete.colors.primary2}
                        className='pr-[32px]'
                      >
                        <div
                          className='max-w-[100%] overflow-hidden text-ellipsis whitespace-nowrap'
                          style={{ padding: '8px 12px', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault()
                            window.open(link, '_blank', 'noopener,noreferrer')
                          }}
                        >
                          <a
                            href={link}
                            style={{
                              textDecorationLine: 'underline',
                              textDecorationColor: pallete.colors.textDark2,
                              color: pallete.colors.primary2
                            }}
                          >
                            {link}
                          </a>
                        </div>
                      </DDSTypography.Paragraph>
                    )
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* {download ? (
            // ✅ This block is rendered inside the PDF ONLY
            <div className='pt-[24px]'>
              <Card>
                <DDSTypography.Title
                  type='h4'
                  variant='bold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Attachments
                </DDSTypography.Title>

                <div className='mt-[16px]'>
                  <a
                    href={blobUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline text-gray-600'
                  >
                    Download Attachments
                  </a>

                  <DDSTypography.Paragraph
                    size='para'
                    className='text-sm mt-2 block'
                  >
                    Tip: Right-click and open the link in a new tab to download
                    all attachments.
                  </DDSTypography.Paragraph>
                </div>
              </Card>
            </div>
          ) : (
            // ✅ This block is rendered in the actual app view (outside the PDF)
            incidentDetails?.attachedFiles?.length > 0 && (
              <div className='pt-[24px]'>
                <Card>
                  <DDSTypography.Title
                    type='h4'
                    variant='bold'
                    color={pallete.colors.textDark3}
                    className='custom-label'
                  >
                    Attachments
                  </DDSTypography.Title>

                  <DDSTypography.Title
                    type='h5'
                    variant='semiBold'
                    color={pallete.colors.textDark5}
                    className='custom-label'
                  >
                    Image uploaded
                  </DDSTypography.Title>

                  <div className='flex flex-wrap gap-[12px] pt-[12px] rounded-lg w-full'>
                    <MediaPreviewGrid
                      attachedFiles={incidentDetails?.attachedFiles}
                      onPreview={(media) => handlePreview(media)}
                      variant='default'
                    />
                  </div>

                  <div className='flex flex-wrap pt-[12px] gap-[12px]'>
                    {incidentDetails.attachedFiles.map((media: any) => {
                      const isImage = media.includes('image')
                      const isVideo = media.includes('video')
                      const fileInfo = parseFileInfoFromUrl(media)

                      return (
                        <React.Fragment key={media}>
                          {!isImage && !isVideo && (
                            <FilePreviewCard
                              file={{
                                name: fileInfo.name,
                                type: fileInfo.type,
                                url: media,
                                size: convertStorage(fileInfo.size)
                              }}
                              uploadType='display'
                            />
                          )}
                        </React.Fragment>
                      )
                    })}
                  </div>

                  <MediaPreviewModal
                    isPreviewOpen={isPreviewOpen}
                    handleClosePreview={handleClosePreview}
                    previewFile={previewFile}
                  />
                </Card>
              </div>
            )
          )} */}

          {!download && incidentDetails?.attachedFiles?.length > 0 && (
            <div className='pt-[24px]'>
              <Card>
                <DDSTypography.Title
                  type='h4'
                  variant='bold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Attachments
                </DDSTypography.Title>

                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  color={pallete.colors.textDark5}
                  className='custom-label'
                >
                  Image uploaded
                </DDSTypography.Title>

                <div className='flex flex-wrap gap-[12px] pt-[12px] rounded-lg w-full'>
                  <MediaPreviewGrid
                    attachedFiles={incidentDetails?.attachedFiles}
                    onPreview={(media) => handlePreview(media)}
                    variant='default'
                  />
                </div>

                <div className='flex flex-wrap pt-[12px] gap-[12px]'>
                  {incidentDetails.attachedFiles.map((media: any) => {
                    const isHeic =
                      media.includes('heic') ||
                      media.includes('heif') ||
                      media.endsWith('.heic') ||
                      media.endsWith('.heif')
                    const isImage = media.includes('image/') && !isHeic
                    const isVideo = media.includes('video')
                    const fileInfo = parseFileInfoFromUrl(media)

                    return (
                      <React.Fragment key={media}>
                        {!isImage && !isVideo && (
                          <FilePreviewCard
                            file={{
                              name: fileInfo.name,
                              type: fileInfo.type,
                              url: media,
                              size: convertStorage(fileInfo.size)
                            }}
                            uploadType='display'
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>

                <MediaPreviewModal
                  isPreviewOpen={isPreviewOpen}
                  handleClosePreview={handleClosePreview}
                  previewFile={previewFile}
                />
              </Card>
            </div>
          )}

          {!download && incidentDetails?.activityCount > 0 && (
            <div className='py-[24px]'>
              <Card>
                <DDSTypography.Title
                  type='h4'
                  variant='bold'
                  color={pallete.colors.textDark3}
                  className='custom-label'
                >
                  Activity
                </DDSTypography.Title>

                <div className='flex flex-col my-[8px]'>
                  {activityMessages && Array.isArray(activityMessages)
                    ? activityMessages.map((item: any) => {
                        const {
                          isPublic,
                          actionedByUser,
                          createdAt,
                          message,
                          data,
                          rootCause,
                          solution,
                          id,
                          attachedFiles
                        } = item

                        if (!isPublic) return null

                        const renderMessageContent = () => {
                          try {
                            const parsed = JSON.parse(message || '')
                            if (parsed?.ops) {
                              return (
                                <div>
                                  {attachedFiles?.length > 0 && (
                                    <div>
                                      <DDSTypography.Paragraph
                                        size='para'
                                        variant='regular'
                                        color={pallete.colors.textDark10}
                                        className='pb-[8px]'
                                        style={{ wordBreak: 'break-word' }}
                                      >
                                        <span>
                                          {actionedByUser?.firstName +
                                            ' ' +
                                            actionedByUser?.lastName}
                                        </span>{' '}
                                        added attachment
                                        {attachedFiles.length > 1 ? 's' : ''}.
                                      </DDSTypography.Paragraph>
                                    </div>
                                  )}
                                  <ReadOnlyQuill
                                    download={download}
                                    commentId={id}
                                    content={
                                      message.includes('ops')
                                        ? new Delta(parsed)
                                        : new Delta().insert(message + '\n')
                                    }
                                    incidentActivity={item}
                                  />
                                </div>
                              )
                            }
                          } catch (err) {
                            console.error(
                              'If parsing fails, treat message as plain text'
                            )
                          }
                          const fromToRendered = renderWithFromToChip(message)

                          return (
                            <div>
                              <DDSTypography.Paragraph
                                size='para'
                                variant='regular'
                                color={pallete.colors.textDark5}
                                className='flex flex-wrap items-center gap-[4px]'
                              >
                                {fromToRendered ||
                                  renderWithIndividualChips(message)}
                              </DDSTypography.Paragraph>
                              {data?.current === 'Resolved' &&
                                message.toLowerCase().endsWith('resolved') && (
                                  <div className='flex flex-col gap-[8px]'>
                                    {rootCause && (
                                      <div className='pt-[8px]'>
                                        <DDSTypography.Paragraph
                                          size='para'
                                          variant='regular'
                                          color={pallete.colors.textDark10}
                                          style={{ wordBreak: 'break-word' }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              fontSize: '14px',
                                              color: pallete.colors.textDark5
                                            }}
                                          >
                                            Root Cause :
                                          </span>{' '}
                                          {rootCause}
                                        </DDSTypography.Paragraph>
                                      </div>
                                    )}

                                    {solution && (
                                      <div>
                                        <DDSTypography.Paragraph
                                          size='para'
                                          variant='regular'
                                          color={pallete.colors.textDark10}
                                          style={{ wordBreak: 'break-word' }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              fontSize: '14px',
                                              color: pallete.colors.textDark5
                                            }}
                                          >
                                            Solution:
                                          </span>{' '}
                                          {solution}
                                        </DDSTypography.Paragraph>
                                      </div>
                                    )}
                                  </div>
                                )}
                            </div>
                          )
                        }

                        return (
                          <div
                            key={id}
                            className='flex flex-col justify-between w-full mb-[16px]'
                          >
                            <div className='flex flex-row w-min items-center gap-x-[8px]'>
                              <DDSTypography.Title
                                type='h5'
                                variant='bold'
                                className='whitespace-nowrap break-words'
                                color={pallete.colors.textDark4}
                              >
                                {actionedByUser?.firstName +
                                  ' ' +
                                  actionedByUser?.lastName}
                              </DDSTypography.Title>
                              <DDSTypography.Paragraph
                                size='caption'
                                variant='regular'
                                style={{ color: pallete.colors.textDark7 }}
                                className='whitespace-nowrap break-words'
                              >
                                {getUserTimeZoneDate(createdAt, orgTimeZone)}
                              </DDSTypography.Paragraph>
                            </div>

                            <div className='mt-[4px]'>
                              {renderMessageContent()}
                            </div>
                          </div>
                        )
                      })
                    : null}

                  <div className='flex flex-wrap gap-[12px] rounded-lg w-full'>
                    <MediaPreviewGrid
                      attachedFiles={activityMessages?.attachedFiles}
                      onPreview={(media) => handlePreview(media)}
                      variant='default'
                    />
                  </div>

                  <div className='flex flex-wrap pb-[12px] gap-[12px]'>
                    {activityMessages?.attachedFiles?.map((media: any) => {
                      const isHeic =
                        media.includes('heic') ||
                        media.includes('heif') ||
                        media.endsWith('.heic') ||
                        media.endsWith('.heif')
                      const isImage = media.includes('image/') && !isHeic
                      const isVideo = media.includes('video')
                      const fileInfo = parseFileInfoFromUrl(media)

                      return (
                        <React.Fragment key={media}>
                          {!isImage && !isVideo && (
                            <FilePreviewCard
                              file={{
                                name: fileInfo.name,
                                type: fileInfo.type,
                                url: media,
                                size: convertStorage(fileInfo.size)
                              }}
                              uploadType='display'
                            />
                          )}
                        </React.Fragment>
                      )
                    })}
                  </div>

                  <MediaPreviewModal
                    isPreviewOpen={isPreviewOpen}
                    handleClosePreview={handleClosePreview}
                    previewFile={previewFile}
                  />
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default IncidentOverView
